import NextApp, { Container } from 'next/app';
import React from 'react';
import { Provider } from 'react-redux';
import storeProvider from '../utils/storeProvider';
import { setRequestContext, setRequestDetails } from '../actions/requestActions';
import { trackSessionStarted } from '../actions/tracking/session';
import sentry from '../utils/sentry';
import extractUserIdentity from '../utils/extractUserIdentity';
import valueFromHeader from '../utils/valueFromHeader';
import getSafe from '../utils/getSafe';

const { setSentryContext } = sentry();

const getSentryContext = (context) => {
  const res = {};
  if (!context.req) return undefined;

  const unwrapReq = ({
    url,
    method,
    headers,
    params,
    query,
    cookies,
    locale,
    country,
    sentryTransaction,
  }) => ({
    url,
    method,
    headers,
    params,
    query,
    cookies,
    locale,
    country,
    sentryTransaction,
  });

  if (context.res && res.statusCode) {
    res.statusCode = context.res.scope;
  }

  const consumerAuthToken =
    getSafe(() => context.req.cookies.consumer_auth_token) &&
    valueFromHeader(context.req.cookies.consumer_auth_token);
  const unverifiedUserId = extractUserIdentity(consumerAuthToken);

  return {
    req: unwrapReq(context.req),
    res,
    pathname: context.pathname,
    unverifiedUserId,
  };
};

class App extends NextApp {
  static async getInitialProps({ Component, ctx }) {
    const { req, query, asPath } = ctx;
    const isServer = Boolean(req);
    const props = {
      initialState: null,
      translations: {},
      additionalProps: {},
      sentryContext: getSentryContext(ctx),
    };

    setSentryContext(props.sentryContext);

    if (typeof Component.initialiseI18n === 'function') {
      props.translations = Component.initialiseI18n(ctx);
    }

    /**
     * If a page provides a getInitialProps function it implies
     * that it doesn't want to be wrapped in redux or the like so we
     * revert to the normal next behaviour of just calling getInitialProps
     * and then passing the return value on to our page.
     */
    if (typeof Component.getInitialProps === 'function') {
      props.additionalProps = await Component.getInitialProps(ctx);
      return props;
    }

    // Get or Create the store
    const store = storeProvider(undefined, isServer);
    store.dispatch(setRequestDetails(asPath, query));

    if (isServer) {
      store.dispatch(setRequestContext(req));
      store.dispatch(trackSessionStarted());
    }

    if (typeof Component.getPageData === 'function') {
      const getPageDataCall = Component.getPageData(ctx, store.dispatch, store.getState);
      if (isServer) {
        await getPageDataCall;
      }
    }

    props.initialState = store.getState();
    return props;
  }

  constructor(props) {
    super(props);
    if (props.initialState) {
      this.store = storeProvider(props.initialState);
    }

    setSentryContext(props.sentryContext);
  }

  renderPageComponent() {
    const { Component, pageProps, additionalProps, translations } = this.props;
    return <Component {...pageProps} {...additionalProps} translations={translations} />;
  }

  render() {
    if (!this.store) {
      return this.renderPageComponent();
    }

    return (
      <Container>
        <Provider store={this.store}>{this.renderPageComponent()}</Provider>
      </Container>
    );
  }
}

export default App;
